import React from 'react';
import t from 'react-translate';
import moment from 'moment';

// redux
import { ComponentType, NLectureComponent, QuestionSetType } from 'redux/schemas/models/lecture-component';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/schemas';
import { getComponentActivity } from 'redux/selectors/quizzes';

import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import ActivityStatus from 'shared/components/activity/activity-status';
import ActivityTitle from 'shared/components/activity/activity-title';
import LectureComponentDeadline from 'components/lecture-component-deadline';
import { NLecturePage } from 'redux/schemas/models/lecture-page';
import { LecturePageMode } from 'lecture_pages/components';
import PointsComponent from './points-component';

type QuestionSetTitleContentProps = {
  type: QuestionSetType,
  mode: LecturePageMode,
  currentLecture: NLecturePage,
  saveComponent: Function,
  lectureComponent: NLectureComponent<QuestionSetType>;
};
const QuestionSetTitleContent = (props: QuestionSetTitleContentProps) => {
  const { type, mode, currentLecture, saveComponent, lectureComponent } = props;
  const isEditMode = mode === LecturePageMode.EDIT;
  const isDeadlineEditable = mode === LecturePageMode.EDIT || mode === LecturePageMode.LINKED_EDIT;

  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));

  const lastSavedAt = activity?.unsubmittedSubmission?.lastSavedAt;
  const isUnlimitedAttempts = activity?.maximumAttempts === 0 || !activity?.maximumAttempts;

  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  const [title, setTitle] = React.useState<string>(activity?.title);

  const handleTitleInputChange = (newTitle: string) => setTitle(newTitle);

  const handleTitleBlur = () => saveComponent({
    title,
  });

  const getComponentName = () => {
    if (type === ComponentType.QUIZ) {
      return 'quiz';
    }
    return 'progressive-quiz';
  };

  const getNotStartedStatus = () => {
    switch (type) {
      case ComponentType.QUIZ:
      {
        if (isUnlimitedAttempts) {
          return t.QUIZZES.PRACTICE();
        }
        return t.QUIZZES.TAKE();
      }
      case ComponentType.PROGRESSIVE_QUIZ:
        return t.QUIZZES.TAKE();
      default:
        return t.QUIZZES.TAKE();
    }
  };

  const getCompletedStatus = () => {
    switch (type) {
      case ComponentType.QUIZ:
      case ComponentType.PROGRESSIVE_QUIZ:
      {
        if (isUnlimitedAttempts) {
          return t.FORM.DONE();
        }
        return t.FORM.SUBMITTED();
      }
      default:
        return t.FORM.SUBMITTED();
    }
  };

  return (
    <React.Fragment>
      <div className='mb-2'>
        <ActivityStatus icon='quiz' status={activity?.progress} />
      </div>
      <ActivityTitle
        status={activity?.progress}
        statusesTexts={{
          not_started: getNotStartedStatus(),
          completed: getCompletedStatus(),
        }}
      />
      <div className={`${(type === ComponentType.PROGRESSIVE_QUIZ) ? 'mb-6' : ''} d-flex flex-column align-items-center w-100`}>
        <LectureComponentHeaderInput
          required
          value={title}
          editable={isEditMode}
          onBlur={handleTitleBlur}
          onChange={handleTitleInputChange}
        />
        { activity?.unsubmittedSubmission && (
          <div className='mb-2 bold text-success text-small'>
            {t.QUIZZES.SAVED(moment(lastSavedAt).format('MMM D, YYYY, hh:mm A'))}
          </div>
        )}
        <PointsComponent
          type={type}
          mode={mode}
          lectureComponent={lectureComponent}
        />
        {lectureComponent.type === ComponentType.SURVEY
          && activity.progress === 'completed'
          && (<p className='text-success semi-bold'>{t.SURVEYS.THANKS_FOR_RESPONSE()}</p>)}
        <div className={activity?.expirationDate ? 'mt-2' : ''}>
          <LectureComponentDeadline
            isEdit={isEditMode}
            catalogId={catalogId}
            componentName={getComponentName()}
            lecturePageId={currentLecture.id}
            expirationDate={activity?.expirationDate}
            hasHardDeadlines={activity?.hardDeadline}
            onHardDeadlineChange={(hardDeadline) => {
              saveComponent({ hardDeadline });
            }}
            onDeadlineChange={(date) => {
              saveComponent({ expirationDate: date?.toISOString() });
            }}
            isDeadlineEditable={isDeadlineEditable}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default QuestionSetTitleContent;
